/**
 * Universal Input Sanitization, Anti-SQL Injection & Anti-Hacking Shield
 * Cameroon Land Market Platform
 * 
 * Provides defense-in-depth against:
 * 1. SQL Injection (e.g., UNION SELECT, OR 1=1, DROP TABLE, comment truncations)
 * 2. NoSQL Operator Injection (e.g., $gt, $ne, $where, $regex)
 * 3. Cross-Site Scripting (XSS), script tags, HTML event handlers, and data: URIs
 * 4. Search query poisoning and null-byte injection
 */

// SQL Injection attack signatures and keywords (using 'i' without 'g' to prevent stateful regex bugs)
const SQL_INJECTION_PATTERNS = [
  /(\b(?:select|union|insert|update|delete|drop|alter|truncate|create|grant|revoke|exec|execute)\b\s+.*?\b(?:from|into|table|database|schema|where)\b)/i,
  /\bunion\s+(?:all\s+)?select\b/i,
  /['"]\s*(?:or|and)\s*['"]?[0-9a-zA-Z]+['"]?\s*=\s*['"]?[0-9a-zA-Z]+['"]?/i,
  /\b(?:or|and)\s+[0-9]+\s*=\s*[0-9]+/i,
  /;\s*(?:drop|delete|truncate|alter|update|insert)\b/i,
  /(?:--|\/\*|\*\/|#\s*$)/,
  /\bxp_[a-zA-Z0-9_]+/i,
  /\bwaitfor\s+delay\b/i,
  /\bbenchmark\s*\(/i,
  /\bsleep\s*\(/i,
];

// NoSQL operator injection patterns
const NOSQL_INJECTION_PATTERNS = [
  /^\$(?:gt|gte|lt|lte|ne|nin|in|where|regex|exists|or|and|nor|not|mod|all|size|elemMatch)\b/i,
  /["']\s*\$(?:gt|gte|lt|lte|ne|nin|in|where|regex)\b/i,
];

/**
 * Checks whether an input contains known SQL or NoSQL injection signatures.
 */
export function isSqlOrNoSqlInjection(input?: string | null): boolean {
  if (!input || typeof input !== 'string') return false;
  const normalized = input.trim();
  for (const pattern of SQL_INJECTION_PATTERNS) {
    if (pattern.test(normalized)) {
      return true;
    }
  }
  for (const pattern of NOSQL_INJECTION_PATTERNS) {
    if (pattern.test(normalized)) {
      return true;
    }
  }
  return false;
}

/**
 * Strips all HTML tags and leaves clean text
 */
export function stripHtml(input?: string | null): string {
  if (!input || typeof input !== 'string') return '';
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\0/g, '')
    .trim();
}

/**
 * Comprehensive Anti-Injection & Anti-Hacking Sanitizer
 * Strips SQL injection meta-syntax, NoSQL operators, script tags, event handlers, and null bytes.
 */
export function sanitizeAntiInjection(input?: string | null, maxLength = 5000): string {
  if (!input || typeof input !== 'string') return '';
  
  let clean = input
    // 1. Remove null bytes and non-printable control characters (except newline and tab)
    .replace(/\0/g, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // 2. Remove script, style, and iframe tags entirely
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    // 3. Remove dangerous HTML event handlers (onload=, onerror=, etc.)
    .replace(/on\w+\s*=\s*(["'][^"']*["']|[^\s>]+)/gi, '')
    // 4. Remove dangerous pseudo-protocols
    .replace(/javascript\s*:/gi, '')
    .replace(/vbscript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
    // 5. Neutralize SQL Injection constructs
    .replace(/\bunion\s+(?:all\s+)?select\b/gi, '')
    .replace(/;\s*(?:drop|delete|truncate|alter)\s+table\b/gi, '')
    .replace(/;\s*exec(?:ute)?\s+/gi, '')
    .replace(/\bxp_[a-zA-Z0-9_]+\b/gi, '')
    .trim();

  // Enforce boundary length
  if (maxLength && maxLength > 0 && clean.length > maxLength) {
    clean = clean.substring(0, maxLength);
  }

  return clean;
}

/**
 * Sanitizes user search terms against query injection and control characters
 */
export function sanitizeSearchTerm(input?: string | null, maxLength = 100): string {
  if (!input || typeof input !== 'string') return '';
  let clean = input
    .replace(/[\0\r\n\t]/g, ' ')
    .replace(/[<>{}\\]/g, '')
    .replace(/--/g, '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/['";]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean.length > maxLength) {
    clean = clean.substring(0, maxLength);
  }
  return clean;
}

/**
 * Sanitizes text content, escaping HTML special entities to prevent XSS
 */
export function sanitizeText(input?: string | null, maxLength?: number): string {
  if (!input || typeof input !== 'string') return '';
  return sanitizeAntiInjection(input, maxLength);
}

/**
 * Validates and sanitizes email address
 */
export function sanitizeEmail(email?: string | null): string {
  if (!email || typeof email !== 'string') return '';
  const clean = email.trim().toLowerCase();
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  return emailRegex.test(clean) && clean.length <= 150 ? clean : '';
}

/**
 * Validates and sanitizes phone number (Cameroonian or international standard format)
 */
export function sanitizePhone(phone?: string | null): string {
  if (!phone || typeof phone !== 'string') return '';
  const clean = phone.replace(/[^0-9+]/g, '').trim();
  return clean.length <= 25 ? clean : clean.substring(0, 25);
}

/**
 * Validates safe external or internal URL (rejects javascript:, vbscript:, data: URIs)
 */
export function isSafeUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith('javascript:') || trimmed.startsWith('vbscript:') || trimmed.startsWith('data:text/html')) {
    return false;
  }
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/') || trimmed.startsWith('tel:') || trimmed.startsWith('mailto:') || trimmed.startsWith('https://wa.me/');
}
